import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { GeneratingDots } from '@/components/glitch-ui/generating-dots';

interface GenerateItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  placeholder?: string;
  submitLabel: string;
  isGenerating: boolean;
  onGenerate: (context: string) => void;
}

/**
 * Shared "describe it (optional) → AI generates → appended" modal for the on-demand Add Product (#5378)
 * and Add Customer Type (#5377) flows. The optional textarea hint steers the generation; while the
 * single backend LLM call runs, the modal shows a generating state. The parent closes it on success.
 */
export function GenerateItemModal({
  open,
  onOpenChange,
  title,
  description,
  placeholder,
  submitLabel,
  isGenerating,
  onGenerate,
}: GenerateItemModalProps) {
  const [context, setContext] = useState('');

  const handleOpenChange = (next: boolean) => {
    if (isGenerating) return; // don't allow dismissing mid-generation
    if (!next) setContext('');
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {isGenerating ? (
          <div className="flex flex-col items-center justify-center gap-4 py-10">
            <GeneratingDots count={5} />
            <p className="text-sm text-gray-500">Generating…</p>
          </div>
        ) : (
          <Textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={placeholder}
            rows={4}
            className="resize-none"
            autoFocus
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isGenerating}>
            Cancel
          </Button>
          <Button
            onClick={() => onGenerate(context.trim())}
            disabled={isGenerating}
            className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
